import { useState, useEffect } from 'react'
import { X, Loader2, AlertCircle } from 'lucide-react'
import { useApp } from '../App'
import { orderingAPI, recipesAPI } from '../lib/api'
import { menuScaleTargetForRecipe } from '../lib/batch'
import { tr, trIngredient, trLabel } from '../lib/translations'
import { friendlyMeasure } from '../lib/kitchenWorkflow'

// ══════════════════════════════════════════════════════════════════════════════
// RECIPE SHEET — read-only recipe for the person holding the knife
// ══════════════════════════════════════════════════════════════════════════════
//
// Opened by tapping an ingredient on the Prep tab. Deliberately not the Recipes
// page modal: no scaling controls, no editing, no print sub-menu — just batch
// size, ingredients and the instructions, at a size that reads from arm's length
// with an iPad on the counter.

export default function RecipeSheetModal({ recipeId, menuId, orderingData, fallbackName, isSpanish, translations, onClose }) {
  const { currentMenu } = useApp()
  const activeMenuId = menuId ?? currentMenu
  const [recipe, setRecipe] = useState(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [retry, setRetry] = useState(0)

  const t = (english) => trLabel(isSpanish, translations, english)

  useEffect(() => {
    // Opened from a component with no recipe linked at all — show the empty state
    // rather than making the row untappable, so the cook gets an answer either way.
    if (!recipeId) {
      setRecipe(null)
      setFailed(false)
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setRecipe(null)
    setFailed(false)
    ;(async () => {
      try {
        const original = await recipesAPI.get(recipeId)
        if (!original?.name || !activeMenuId) throw new Error('Recipe or current menu unavailable')
        const ordering = orderingData || await orderingAPI.calculate(activeMenuId)
        const target = menuScaleTargetForRecipe(original, ordering)
        if (!(target > 0)) throw new Error('No current-menu scaling target')
        const scaled = await recipesAPI.scale(original.db_id ?? original.id, target)
        if (cancelled) return
        setRecipe({
          ...original,
          base_batch_size: scaled.scaled_batch_size,
          base_batch_unit: scaled.scaled_batch_unit,
          ingredients: scaled.ingredients,
          scale_factor: scaled.scale_factor,
        })
      } catch (error) {
        console.warn(`Could not load a menu-scaled recipe for ${recipeId}:`, error)
        if (!cancelled) setFailed(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [recipeId, activeMenuId, orderingData, retry])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const rid = recipe ? (recipe.db_id || recipe.id) : null
  const name = recipe ? tr(isSpanish, translations, 'recipes', rid, 'name', recipe.name) : fallbackName
  const prep = recipe ? tr(isSpanish, translations, 'recipes', rid, 'prep_instructions', recipe.prep_instructions) : ''
  const cook = recipe ? tr(isSpanish, translations, 'recipes', rid, 'cook_instructions', recipe.cook_instructions) : ''
  const notes = recipe ? tr(isSpanish, translations, 'recipes', rid, 'chef_notes', recipe.chef_notes) : ''
  const allergens = recipe?.allergens || []

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/50 flex items-end sm:items-center justify-center sm:p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-label={name || t('Recipe')}
    >
      <div className="bg-white w-full sm:rounded-2xl sm:max-w-2xl max-h-[92vh] flex flex-col shadow-elevated rounded-t-2xl">
        {/* Header — stays put while the instructions scroll */}
        <div className="flex items-start gap-3 px-5 py-4 border-b border-havn-cream-200 flex-shrink-0">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-widest text-havn-gray-400">{t('Recipe')}</p>
            <h2 className="font-display text-xl font-semibold text-havn-green-900 leading-tight">{name}</h2>
            {recipe?.base_batch_size ? (
              <p className="text-sm text-havn-gray-500 mt-0.5">
                {t('Scaled batch')}: <span className="tabular-nums font-medium text-havn-charcoal">{friendlyMeasure(recipe.base_batch_size, recipe.base_batch_unit)}</span>
              </p>
            ) : null}
          </div>
          <button
            onClick={onClose}
            aria-label={t('Close')}
            className="flex-shrink-0 w-11 h-11 rounded-xl border border-havn-cream-300 flex items-center justify-center text-havn-gray-500 hover:bg-havn-cream-50 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-havn-green-700 animate-spin" />
            </div>
          ) : failed || !recipe ? (
            <div className="py-10 text-center">
              <AlertCircle className="w-8 h-8 text-havn-gray-400 mx-auto mb-3" />
              <p className="text-sm text-havn-gray-500">
                {failed ? t("Couldn't load this recipe.") : t('No recipe linked to this ingredient yet.')}
              </p>
              {failed && <button onClick={() => setRetry((n) => n + 1)} className="btn-secondary mt-3">{t('Retry')}</button>}
            </div>
          ) : (
            <>
              {allergens.length > 0 && (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <strong>{t('Allergens')}:</strong> {allergens.join(', ')}
                </p>
              )}

              {recipe.ingredients?.length > 0 && (
                <section>
                  <h3 className="text-[11px] uppercase tracking-widest text-havn-gray-400 mb-2">{t('Ingredients')}</h3>
                  <table className="w-full">
                    <tbody>
                      {recipe.ingredients.map((ing, i) => (
                        <tr key={`${ing.ingredient_name}-${i}`} className="border-b border-havn-cream-100 last:border-b-0">
                          <td className="py-2 pr-3 text-base text-havn-charcoal">
                            {trIngredient(isSpanish, translations, rid, ing.ingredient_name, ing.ingredient_name)}
                          </td>
                          <td className="py-2 text-right text-base font-medium tabular-nums text-havn-green-900 whitespace-nowrap">
                            {friendlyMeasure(ing.quantity, ing.unit)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}

              {(prep || cook) && <p className="text-sm bg-havn-green-50 text-havn-green-900 p-3 rounded-lg">{t('Use the ingredient quantities above for this batch. Instructions below describe the method; any original batch numbers are reference only.')}</p>}
              {prep && (
                <section>
                  <h3 className="text-[11px] uppercase tracking-widest text-havn-gray-400 mb-2">{t('Preparation method')}</h3>
                  <p className="text-base text-havn-charcoal whitespace-pre-wrap leading-relaxed">{prep}</p>
                </section>
              )}

              {cook && (
                <section>
                  <h3 className="text-[11px] uppercase tracking-widest text-havn-gray-400 mb-2">{t('Cooking method')}</h3>
                  <p className="text-base text-havn-charcoal whitespace-pre-wrap leading-relaxed">{cook}</p>
                </section>
              )}

              {notes && (
                <section className="bg-havn-cream-50 border border-havn-cream-200 rounded-lg px-4 py-3">
                  <h3 className="text-[11px] uppercase tracking-widest text-havn-gray-400 mb-1">{t('Chef Notes')}</h3>
                  <p className="text-base text-havn-charcoal whitespace-pre-wrap leading-relaxed">{notes}</p>
                </section>
              )}

              {!recipe.ingredients?.length && !prep && !cook && !notes && (
                <p className="py-8 text-center text-sm text-havn-gray-500">{t('This recipe has no details entered yet.')}</p>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-havn-cream-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full min-h-[48px] px-4 py-3 bg-havn-green-700 hover:bg-havn-green-800 text-white rounded-xl font-medium transition-colors"
          >
            {t('Close')}
          </button>
        </div>
      </div>
    </div>
  )
}
